import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Facebook, Menu, X, ArrowRight, Sparkles, MessageCircle, MapPin, Phone, User } from "lucide-react";
import { FACEBOOK_URL, MESSENGER_URL, LOGO_URL } from "@/constants/content";
import { useSiteContent } from "@/hooks/useSiteContent";
import { NAV } from "@/constants/testIds";

const NAV_LINKS = [
  { to: "/", label: "Kezdőlap", testId: NAV.home, end: true },
  { to: "/rolunk", label: "Bemutatkozás", testId: "nav-rolunk" },
  { to: "/masszazs", label: "Masszázs & Wellness", testId: NAV.masszazs },
  { to: "/4life", label: "4Life Megoldások", testId: NAV.fourlife },
  { to: "/forever", label: "Forever Arc & Szépségápolás", testId: NAV.forever },
  { to: "/blog", label: "Blog & Videók", testId: NAV.blog },
  { to: "/ajandekkartya", label: "Ajándékkártya", testId: "nav-ajandekkartya" },
];

function Background() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden -z-10">
      <div className="absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-[#2A9D8F]/15 blur-[120px] blob-breathe" />
      <div className="absolute top-1/3 -right-40 h-[600px] w-[600px] rounded-full bg-[#D4AF37]/15 blur-[140px] blob-breathe delay-1" />
      <div className="absolute bottom-0 left-1/4 h-[480px] w-[480px] rounded-full bg-[#5BB5A9]/10 blur-[120px] blob-breathe delay-2" />
    </div>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  return (
    <header
      className={`sticky top-0 z-30 transition-all duration-300 ${
        scrolled
          ? "bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#EAE5D9]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-4 md:py-5 flex items-center justify-between gap-6 lg:gap-8">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="nav-logo-link">
          <img
            src={LOGO_URL}
            alt="TestLélekHarmónia"
            className="h-11 w-11 md:h-12 md:w-12 rounded-full object-cover ring-1 ring-[#D4AF37]/40 shadow-sm"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif-display text-[#1A2825] text-base md:text-lg xl:text-xl tracking-wide whitespace-nowrap">
              TestLélekHarmónia
            </div>
            <div className="text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-[#758C88] whitespace-nowrap">
              Masszázs Stúdió
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 text-sm tracking-wide text-[#3A4D4A]">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                `relative pb-1 transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-[#1C6B61] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:bg-[#D4AF37]"
                    : "hover:text-[#2A9D8F]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={NAV.facebookHeader}
            className="hidden md:inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0e5fcc] text-white px-4 py-2.5 rounded-full text-xs xl:text-sm tracking-wide transition-all shadow-[0_6px_18px_rgba(24,119,242,0.25)] hover:-translate-y-0.5 whitespace-nowrap"
          >
            <Facebook className="h-4 w-4" />
            <span className="hidden xl:inline">Kövess a Facebookon</span>
            <span className="xl:hidden">Facebook</span>
          </a>

          <Link
            to="/#magnetek"
            className="hidden xl:inline-flex items-center gap-1.5 bg-[#2A9D8F] hover:bg-[#1C6B61] text-white px-4 py-2.5 rounded-full text-xs xl:text-sm tracking-wide transition-all shadow-[0_6px_18px_rgba(42,157,143,0.25)] hover:-translate-y-0.5 whitespace-nowrap"
          >
            Ingyenes ajándékok <ArrowRight className="h-3.5 w-3.5" />
          </Link>

          <button
            type="button"
            data-testid={NAV.mobileMenuToggle}
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden h-10 w-10 rounded-full bg-white border border-[#EAE5D9] flex items-center justify-center text-[#1A2825]"
            aria-label="Menü"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid={NAV.mobileMenuPanel}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t border-[#EAE5D9] bg-[#FDFBF7]/95 backdrop-blur-md"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `py-3 px-2 rounded-xl text-base ${
                      isActive ? "text-[#1C6B61] bg-[#F6F3EB]" : "text-[#3A4D4A]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-[#1877F2] text-white px-5 py-3 rounded-full text-sm"
              >
                <Facebook className="h-4 w-4" /> Kövess a Facebookon a napi tippekért
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  const { content } = useSiteContent();
  const STUDIO = content.studio;
  return (
    <footer data-testid="site-footer" className="relative border-t border-[#EAE5D9]/70 mt-20 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO_URL} alt="" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#D4AF37]/40" />
            <div>
              <div className="font-serif-display text-[#1A2825] text-xl">TestLélekHarmónia</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#A88A2B]">Masszázs Stúdió</div>
            </div>
          </div>
          <p className="text-[#3A4D4A] font-light leading-relaxed text-sm">
            A harmónia belül kezdődik — és kívül látszik. Klasszikus masszázs,
            4Life megoldások és napi rituálék egy helyen, a Te jólétedért.
          </p>

          <div className="mt-6 space-y-3 text-sm text-[#3A4D4A]">
            <div className="flex items-start gap-2.5">
              <User className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#A88A2B]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Stúdió</div>
                <div className="font-medium text-[#1A2825]" data-testid="footer-studio-name">
                  {STUDIO.name}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#A88A2B]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Helyszín</div>
                <div className="font-medium text-[#1A2825]" data-testid="footer-studio-address">
                  {STUDIO.address}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <Phone className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#A88A2B]" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Bejelentkezés és elérhetőségek</div>
                <ul className="mt-1 space-y-1.5" data-testid="footer-practitioners">
                  {(STUDIO.practitioners || []).map((p) => (
                    <li key={p.phone} className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                      <span className="font-medium text-[#1A2825]">{p.name}</span>
                      <a
                        href={`tel:${p.phone}`}
                        data-testid={`footer-phone-${p.phone}`}
                        className="text-[#1C6B61] hover:text-[#A88A2B] tracking-wide"
                      >
                        {p.phoneDisplay}
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-xs text-[#758C88]" data-testid="footer-studio-contact">
                  Kapcsolat: {STUDIO.contactNote}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A2B] mb-4">Oldalak</div>
          <ul className="space-y-2 text-sm text-[#3A4D4A]">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-[#2A9D8F] transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A2B] mb-4">Bejelentkezés és kapcsolat</div>
          <div className="flex flex-col gap-3">
            <a
              href={MESSENGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-messenger-button"
              className="inline-flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0e5fcc] text-white px-5 py-3 rounded-full text-sm tracking-wide transition-all shadow-[0_6px_18px_rgba(24,119,242,0.25)] hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" />
              Üzenj a Messengeren
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={NAV.facebookFooter}
              className="inline-flex items-center justify-center gap-2 bg-white border border-[#1877F2]/30 hover:border-[#1877F2] text-[#1877F2] px-5 py-3 rounded-full text-sm tracking-wide transition-all"
            >
              <Facebook className="h-4 w-4" />
              Kövess a Facebookon a napi tippekért!
            </a>
            <Link
              to="/#magnetek"
              className="mt-1 inline-flex items-center gap-2 text-sm text-[#2A9D8F] hover:text-[#1C6B61]"
            >
              <Sparkles className="h-3.5 w-3.5" /> Ingyenes hírlevelek
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-[#EAE5D9]/70">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#758C88]">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <span>© {new Date().getFullYear()} {STUDIO.name} · Minden jog fenntartva</span>
            <span className="hidden sm:inline text-[#EAE5D9]">·</span>
            <Link
              to="/adatkezeles"
              data-testid="footer-privacy-link"
              className="hover:text-[#1C6B61] transition-colors underline underline-offset-2"
            >
              Adatkezelési tájékoztató
            </Link>
            <span className="hidden sm:inline text-[#EAE5D9]">·</span>
            <Link
              to="/hazirend"
              data-testid="footer-hazirend-link"
              className="hover:text-[#1C6B61] transition-colors underline underline-offset-2"
            >
              Házirend
            </Link>
          </div>
          <div className="italic font-serif-display text-[#A88A2B] text-sm">
            „A harmónia belül kezdődik – és kívül látszik.”
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  // Scroll to top on route change
  const loc = useLocation();
  useEffect(() => {
    if (loc.hash) {
      const el = document.querySelector(loc.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [loc.pathname, loc.hash]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#FDFBF7] text-[#3A4D4A]">
      <Background />
      <Nav />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
